import React, { useState, useEffect, useMemo } from "react";
import { FaPlus, FaTrash, FaEdit, FaSearch, FaEye } from "react-icons/fa";
import { useAuth } from '../../context/AuthContext';
import axios from "axios";
import CobroCuentaCorriente from "../cuentaCorriente/CobroCuentaCorrienteCliente.js";
import { toast } from "react-toastify";
import ClienteForm from "./ClienteForm";
import Sheet from "../Sheet.js";


const CompClientes = () => {
  const { token, userId } = useAuth();
  const [clienteCobro, setClienteCobro] = useState(null);
  const [sheetCuentas, setSheetCuentas] = useState(null);
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const [loadingVenta, setLoadingVenta] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [clienteEdit, setClienteEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchClientes = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/clientes`,
        {
          headers: {
            "x-auth-token": token,
          },
        }
      );
      
      console.log(response.data);
      
      // Validación defensiva: asegurar que response.data sea un array
      const clientesData = Array.isArray(response.data) ? response.data : [];
      setClientes(clientesData);
      
      if (!Array.isArray(response.data)) {
        console.warn('La respuesta de clientes no es un array:', response.data);
      }
      
    } catch (err) {
      setError("Error al cargar clientes.");
      console.error('Error fetching clientes:', err);
      setClientes([]); // Establecer array vacío en caso de error
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchClientes();
  }, [token]);
  
  const filteredClientes = useMemo(() => {
    // Validación defensiva: asegurar que clientes sea un array
    if (!Array.isArray(clientes)) {
      console.warn('clientes no es un array:', clientes);
      return [];
    }
    
    return clientes.filter((c) =>
      c && c.nombre && c.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [clientes, searchTerm]);

  // Calcular el total de deudas de todos los clientes
  const totalDeudasGeneral = useMemo(() => {
    if (!Array.isArray(clientes)) return { total: 0, clientesConDeuda: 0, cuentasPendientes: 0 };
    
    let total = 0;
    let clientesConDeuda = 0;
    let cuentasPendientes = 0;
    
    clientes.forEach(cliente => {
      const cuentasConDeuda = (cliente.cuenta_corrientes || []).filter(
        (cc) => parseFloat(cc.saldo_pendiente) > 0
      );
      
      if (cuentasConDeuda.length > 0) {
        clientesConDeuda++;
        cuentasPendientes += cuentasConDeuda.length;
        const totalCliente = cuentasConDeuda.reduce((total, cc) => total + parseFloat(cc.saldo_pendiente), 0);
        total += totalCliente;
      }
    });
    
    return { total, clientesConDeuda, cuentasPendientes };
  }, [clientes]);

  const openNewCliente = () => {
    setClienteEdit(null);
    setIsSheetOpen(true);
  };

  const openEditCliente = (cliente) => {
    setClienteEdit(cliente);
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
  };


  const handleSaveCliente = async (cliente) => {
    setLoading(true);
    setError("");
    try {
      let updatedCliente;
      if (cliente.id) {
        // Editar (PUT)
        await axios.put(
          `${import.meta.env.VITE_API_URL}/clientes/${cliente.id}`,
          cliente,
          {
            headers: {
              "x-auth-token": token,
            },
          }
        );
        // Refresca toda la lista de clientes para asegurar datos actualizados
        await fetchClientes();
      } else {
        // Nuevo (POST)
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/clientes`,
          cliente,
          {
            headers: {
              "x-auth-token": token,
            },
          }
        );
        updatedCliente = response.data;
        setClientes((prev) => [updatedCliente, ...prev]);
      }
      closeSheet();
    } catch (err) {
      setError("Error al guardar cliente.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCliente = async (id) => {
    if (window.confirm("¿Querés eliminar este cliente?")) {
      setLoading(true);
      setError("");
      try {
        await axios.delete(`${import.meta.env.VITE_API_URL}/clientes/${id}`, {
          headers: {
            "x-auth-token": token,
          },
        });
        setClientes((prev) => prev.filter((c) => c.id !== id));
      } catch (err) {
        setError("Error al eliminar cliente.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };
  const caja = localStorage.getItem('cajaAbiertaId');

  const handleCobroCuenta = async (monto, formaPago, responseData) => {
    try {
      // El backend ya procesó el pago, solo necesitamos actualizar la UI
      if (responseData && responseData.resumen) {
        // Refrescar la información completa del cliente
        await fetchClientes();
        
        const cuentasAfectadas = responseData.resumen.cuentas_actualizadas || 0;
        toast.success(
          `Pago procesado exitosamente. Se aplicó $${monto.toFixed(2)} a ${cuentasAfectadas} cuenta(s).`,
          { autoClose: 5000 }
        );
      } else {
        toast.success(`Pago de $${monto.toFixed(2)} procesado exitosamente.`);
        await fetchClientes();
      }
      
      setClienteCobro(null);
    } catch (err) {
      console.error('Error actualizando UI después del cobro:', err);
      toast.error("Pago procesado pero error al actualizar la interfaz");
    }
  };

  // Nueva función para obtener detalles de la venta
  const handleVerVenta = async (idVenta) => {
    setLoadingVenta(true);
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_API_URL}/ventas/admin/${idVenta}`,
        { headers: { "x-auth-token": token } }
      );

      setVentaDetalle(data.venta);
      console.log(data.venta);
      
    } catch (err) {
      toast.error("No se pudo obtener la venta.");
      setVentaDetalle(null);
    } finally {
      setLoadingVenta(false);
    }
  };

  // Función para imprimir ticket de saldos generales con detalles de productos
  const imprimirTicketSaldos = async (cliente, cuentasConDeuda) => {
    const totalDeuda = cuentasConDeuda.reduce((total, cc) => total + parseFloat(cc.saldo_pendiente), 0);
    const fechaActual = new Date().toLocaleString('es-AR');
    
    // Obtener detalles de todas las ventas
    const ventasConDetalles = await Promise.all(
      cuentasConDeuda.map(async (cc) => {
        try {
          const { data } = await axios.get(
            `${import.meta.env.VITE_API_URL}/ventas/admin/${cc.id_venta}`,
            { headers: { "x-auth-token": token } }
          );
          return { ...cc, ventaDetalle: data.venta };
        } catch (err) {
          console.error(`Error obteniendo venta ${cc.id_venta}:`, err);
          return { ...cc, ventaDetalle: null };
        }
      })
    );
    
    const contenidoTicket = `
      <div style="width: 300px; font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; line-height: 1.3;">
        <div style="text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h2 style="margin: 0; font-size: 18px; font-weight: bold;">ZAFPAN</h2>
          <p style="margin: 3px 0; font-size: 12px;">Estado de Cuenta Corriente</p>
          <p style="margin: 0; font-size: 10px;">${fechaActual}</p>
        </div>
        
        <div style="margin-bottom: 12px;">
          <p style="margin: 2px 0;"><strong>Cliente:</strong> ${cliente.nombre}</p>
          ${cliente.DNI ? `<p style="margin: 2px 0;"><strong>DNI:</strong> ${cliente.DNI}</p>` : ''}
          ${cliente.telefono ? `<p style="margin: 2px 0;"><strong>Tel:</strong> ${cliente.telefono}</p>` : ''}
        </div>

        <div style="border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-align: center; font-weight: bold;">DEUDAS PENDIENTES</h3>
          ${ventasConDetalles.map(cc => `
            <div style="margin-bottom: 12px; padding: 6px; border: 1px solid #333;">
              <p style="margin: 1px 0; font-weight: bold;">Venta #${cc.id_venta}</p>
              <p style="margin: 1px 0; font-size: 10px;">Fecha: ${cc.fecha_venta ? new Date(cc.fecha_venta).toLocaleDateString('es-AR') : 'Sin fecha'}</p>
              <p style="margin: 1px 0; font-size: 14px; font-weight: bold;">Saldo: $${parseFloat(cc.saldo_pendiente).toFixed(2)}</p>
              ${cc.descripcion ? `<p style="margin: 1px 0; font-size: 10px;">Obs: ${cc.descripcion}</p>` : ''}
              
              ${cc.ventaDetalle && cc.ventaDetalle.detalles_venta ? `
                <div style="margin-top: 6px; padding: 4px; background: #f8f8f8; border: 1px dashed #666;">
                  <p style="margin: 1px 0; font-size: 10px; font-weight: bold;">PRODUCTOS:</p>
                  ${cc.ventaDetalle.detalles_venta.map(detalle => `
                    <div style="margin: 2px 0; font-size: 9px; padding-left: 4px;">
                      <span style="font-weight: bold;">${detalle.producto?.nombre_producto || 'Producto'}</span><br>
                      <span>${parseFloat(detalle.cantidad)} x $${detalle.precio} = $${parseFloat(detalle.subtotal).toFixed(2)}</span>
                      ${detalle.descuento > 0 ? `<br><span style="color: #888;">Desc: $${detalle.descuento}</span>` : ''}
                    </div>
                  `).join('')}
                  <div style="border-top: 1px dashed #999; margin-top: 4px; padding-top: 2px;">
                    <span style="font-size: 10px; font-weight: bold;">Total Venta: $${cc.ventaDetalle.total}</span>
                  </div>
                </div>
              ` : '<p style="margin: 2px 0; font-size: 9px; color: #666;">Sin detalles de productos disponibles</p>'}
            </div>
          `).join('')}
        </div>

        <div style="text-align: center; font-size: 16px; margin: 12px 0; border: 2px solid #000; padding: 8px;">
          <p style="margin: 0; font-weight: bold;">TOTAL ADEUDADO</p>
          <p style="margin: 2px 0; font-size: 20px; font-weight: bold;">$${totalDeuda.toFixed(2)}</p>
        </div>

        <div style="border-top: 2px dashed #000; padding-top: 8px; text-align: center; font-size: 10px;">
          <p style="margin: 1px 0;">Gracias por su confianza</p>
          <p style="margin: 1px 0;">Para pagos, consulte en el local</p>
        </div>
      </div>
    `;

    // Crear ventana de impresión optimizada
    const ventanaImpresion = window.open('', '_blank', 'width=300,height=600');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ticket - Estado de Cuenta</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px;
              background: white;
              padding: 0;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              @page { 
                size: 80mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          ${contenidoTicket}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    ventanaImpresion.document.close();

    toast.success("Ticket de resumen enviado a impresión");
  };

  // Nueva función para imprimir ticket individual de una cuenta específica con detalles de productos
  const imprimirTicketCuentaIndividual = async (cliente, cuentaCorriente) => {
    const fechaActual = new Date().toLocaleString('es-AR');
    
    // Obtener detalles de la venta
    let ventaDetalle = null;
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_API_URL}/ventas/admin/${cuentaCorriente.id_venta}`,
        { headers: { "x-auth-token": token } }
      );
      ventaDetalle = data.venta;
    } catch (err) {
      console.error(`Error obteniendo venta ${cuentaCorriente.id_venta}:`, err);
    }
    
    const contenidoTicket = `
      <div style="width: 300px; font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; line-height: 1.3;">
        <div style="text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h2 style="margin: 0; font-size: 18px; font-weight: bold;">ZAFPAN</h2>
          <p style="margin: 3px 0; font-size: 12px;">Detalle de Cuenta Corriente</p>
          <p style="margin: 0; font-size: 10px;">${fechaActual}</p>
        </div>
        
        <div style="margin-bottom: 12px;">
          <p style="margin: 2px 0;"><strong>Cliente:</strong> ${cliente.nombre}</p>
          ${cliente.DNI ? `<p style="margin: 2px 0;"><strong>DNI:</strong> ${cliente.DNI}</p>` : ''}
          ${cliente.telefono ? `<p style="margin: 2px 0;"><strong>Tel:</strong> ${cliente.telefono}</p>` : ''}
        </div>

        <div style="border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-align: center; font-weight: bold;">DETALLE DE VENTA</h3>
          
          <div style="margin-bottom: 8px; padding: 6px; border: 2px solid #333;">
            <p style="margin: 2px 0; font-weight: bold; text-align: center;">Venta #${cuentaCorriente.id_venta}</p>
            <p style="margin: 2px 0;"><strong>Fecha Venta:</strong> ${cuentaCorriente.fecha_venta ? new Date(cuentaCorriente.fecha_venta).toLocaleDateString('es-AR') : 'Sin fecha'}</p>
            <p style="margin: 2px 0;"><strong>Saldo Pendiente:</strong> $${parseFloat(cuentaCorriente.saldo_pendiente).toFixed(2)}</p>
            ${cuentaCorriente.descripcion ? `<p style="margin: 2px 0;"><strong>Observaciones:</strong> ${cuentaCorriente.descripcion}</p>` : ''}
          </div>

          ${ventaDetalle ? `
            <div style="margin-bottom: 8px; padding: 6px; border: 1px solid #666;">
              <p style="margin: 2px 0; font-weight: bold;">INFORMACIÓN DE LA VENTA:</p>
              <p style="margin: 1px 0;">Total Original: $${ventaDetalle.total}</p>
              <p style="margin: 1px 0;">Descuento: $${ventaDetalle.descuento}</p>
              <p style="margin: 1px 0;">Forma de Pago: ${ventaDetalle.detalles_pago}</p>
              <p style="margin: 1px 0;">Vendedor ID: #${ventaDetalle.id_vendedor}</p>
              <p style="margin: 1px 0; font-size: 10px;">Fecha: ${ventaDetalle.createdAt ? new Date(ventaDetalle.createdAt).toLocaleDateString('es-AR') : 'Sin fecha'}</p>
            </div>
            
            ${ventaDetalle.detalles_venta && ventaDetalle.detalles_venta.length > 0 ? `
              <div style="margin-bottom: 8px; padding: 6px; border: 1px solid #666; background: #f8f8f8;">
                <p style="margin: 2px 0; font-weight: bold; text-align: center;">PRODUCTOS VENDIDOS:</p>
                <div style="border-top: 1px dashed #999; margin-top: 4px; padding-top: 4px;">
                  ${ventaDetalle.detalles_venta.map(detalle => `
                    <div style="margin: 4px 0; padding: 3px; border-bottom: 1px dotted #ccc;">
                      <p style="margin: 1px 0; font-weight: bold; font-size: 11px;">${detalle.producto?.nombre_producto || 'Producto'}</p>
                      <div style="display: flex; justify-content: space-between; font-size: 10px;">
                        <span>${parseFloat(detalle.cantidad)} x $${detalle.precio}</span>
                        <span style="font-weight: bold;">$${parseFloat(detalle.subtotal).toFixed(2)}</span>
                      </div>
                      ${detalle.descuento > 0 ? `<p style="margin: 1px 0; font-size: 9px; color: #666;">Descuento: $${detalle.descuento}</p>` : ''}
                    </div>
                  `).join('')}
                </div>
                <div style="border-top: 2px solid #333; margin-top: 6px; padding-top: 4px; text-align: center;">
                  <p style="margin: 1px 0; font-weight: bold; font-size: 12px;">TOTAL VENTA: $${ventaDetalle.total}</p>
                  ${ventaDetalle.descuento > 0 ? `<p style="margin: 1px 0; font-size: 10px; color: #666;">Descuento Total: $${ventaDetalle.descuento}</p>` : ''}
                </div>
              </div>
            ` : '<p style="margin: 2px 0; font-size: 10px; color: #666;">Sin detalles de productos disponibles</p>'}
          ` : '<p style="margin: 2px 0; font-size: 10px; color: #666;">No se pudieron cargar los detalles de la venta</p>'}
        </div>

        <div style="text-align: center; font-size: 16px; margin: 12px 0; border: 2px solid #000; padding: 8px;">
          <p style="margin: 0; font-weight: bold;">MONTO ADEUDADO</p>
          <p style="margin: 2px 0; font-size: 20px; font-weight: bold;">$${parseFloat(cuentaCorriente.saldo_pendiente).toFixed(2)}</p>
        </div>

        <div style="border-top: 2px dashed #000; padding-top: 8px; text-align: center; font-size: 10px;">
          <p style="margin: 1px 0;">Gracias por su confianza</p>
          <p style="margin: 1px 0;">Para pagos, consulte en el local</p>
          <p style="margin: 1px 0;">Ticket generado: ${fechaActual}</p>
        </div>
      </div>
    `;

    // Crear ventana de impresión
    const ventanaImpresion = window.open('', '_blank', 'width=300,height=600');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ticket - Cuenta Individual</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px;
              background: white;
              padding: 0;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              @page { 
                size: 80mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          ${contenidoTicket}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    ventanaImpresion.document.close();

    toast.success(`Ticket de Venta #${cuentaCorriente.id_venta} enviado a impresión`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header principal */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center gap-4 mb-6">
       
            <div className="flex-1">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Gestión de Clientes
              </h1>
              <p className="text-gray-600">Administra tus clientes y sus cuentas corrientes</p>
            </div>
          </div>

          {/* Resumen de deudas totales */}
          {totalDeudasGeneral.total > 0 && (
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-4">
               
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-red-800">
                      Total de Deudas Pendientes
                    </h2>
                    <div className="text-sm text-red-600 bg-red-100 px-3 py-1 rounded-full border border-red-200">
                      {((totalDeudasGeneral.clientesConDeuda / clientes.length) * 100).toFixed(1)}% de clientes con deuda
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <p className="text-sm text-red-600 font-medium">💰 Monto Total</p>
                      <p className="text-2xl font-bold text-red-700">
                        ${totalDeudasGeneral.total.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-orange-200">
                      <p className="text-sm text-orange-600 font-medium">👥 Clientes con Deuda</p>
                      <p className="text-2xl font-bold text-orange-700">
                        {totalDeudasGeneral.clientesConDeuda}
                        <span className="text-sm font-normal text-gray-500 ml-1">
                          de {clientes.length}
                        </span>
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-sm text-amber-600 font-medium">📋 Cuentas Pendientes</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {totalDeudasGeneral.cuentasPendientes}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buscador y botón agregar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar cliente por nombre..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring focus:ring-purple-200 transition-all duration-200 bg-gray-50 hover:bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={openNewCliente}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all duration-200 shadow flex items-center gap-2"
            >
              <FaPlus className="text-lg" />
              Agregar Cliente
            </button>
          </div>
        </div>

        {/* Grid de clientes */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredClientes.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-gray-400 text-4xl">👥</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No se encontraron clientes</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? `No hay clientes que coincidan con "${searchTerm}"` 
                : 'Aún no has agregado ningún cliente'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={openNewCliente}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-6 py-3 rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <FaPlus className="inline mr-2" />
                Agregar tu primer cliente
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClientes.map((cliente) => {
              // Filtra solo cuentas con deuda pendiente
              const cuentasConDeuda = (cliente.cuenta_corrientes || []).filter(
                cc => parseFloat(cc.saldo_pendiente) > 0
              );
              const totalDeuda = cuentasConDeuda.reduce((total, cc) => total + parseFloat(cc.saldo_pendiente), 0);
              
              return (
                <div
                  key={cliente.id}
                  className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-xl hover:border-purple-200 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  {/* Header del cliente */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{cliente.nombre}</h3>
                      <p className="text-sm text-gray-600">DNI: {cliente.DNI || 'No especificado'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditCliente(cliente)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 border border-blue-100"
                        title="Editar cliente"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteCliente(cliente.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-200 border border-red-100"
                        title="Eliminar cliente"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  {/* Información de contacto */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">📞</span>
                        <span className="text-gray-700">{cliente.telefono || 'Sin teléfono'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">🏠</span>
                        <span className="text-gray-700">{cliente.direccion || 'Sin dirección'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Estado de cuenta corriente */}
                  {cuentasConDeuda.length > 0 ? (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-600 mb-1">💸 Deuda Total</p>
                          <p className="text-xl font-bold text-red-700">${totalDeuda.toFixed(2)}</p>
                          <p className="text-xs text-red-600">
                            {cuentasConDeuda.length} cuenta{cuentasConDeuda.length !== 1 ? 's' : ''} pendiente{cuentasConDeuda.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="bg-red-500 p-2 rounded-full">
                          <span className="text-white text-lg">⚠️</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-green-600 text-lg">✅</span>
                        <span className="text-green-700 font-medium">Sin deudas pendientes</span>
                      </div>
                    </div>
                  )}

                  {/* Acciones */}
                  <div className="flex flex-col gap-2">
                    {cuentasConDeuda.length > 0 && (
                      <>
                        <button
                          onClick={() => setSheetCuentas({ cliente, cuentas: cuentasConDeuda })}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-xl font-bold transition-all duration-200 shadow flex items-center justify-center gap-2"
                        >
                          <span className="text-lg">💳</span>
                          Ver boletas pendientes
                        </button>
                        <button
                          onClick={async () => {
                            toast.info("Generando ticket con detalles de productos...");
                            await imprimirTicketSaldos(cliente, cuentasConDeuda);
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl font-bold transition-all duration-200 shadow flex items-center justify-center gap-2"
                        >
                          <span className="text-lg">🖨️</span>
                          Imprimir Resumen
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sheets */}
      {isSheetOpen && (
        <Sheet isOpen={isSheetOpen} onClose={closeSheet}>
          <ClienteForm
            cliente={clienteEdit}
            onSave={handleSaveCliente}
            onCancel={closeSheet}
          />
        </Sheet>
      )}

      {clienteCobro && (
        <Sheet isOpen={!!clienteCobro} onClose={() => setClienteCobro(null)} size="fullscreen">
          <CobroCuentaCorriente
            cliente={clienteCobro.cliente}
            onCobroCuenta={handleCobroCuenta}
            onClose={() => setClienteCobro(null)}
            caja={caja}
          />
        </Sheet>
      )}

      {/* Sheet para mostrar las cuentas corrientes pendientes del cliente */}
      {sheetCuentas && (
        <Sheet isOpen={!!sheetCuentas} onClose={() => setSheetCuentas(null)} size="large">
          <div className="min-h-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-6">
            {/* Header mejorado */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-xl">
                    <span className="text-white text-2xl">💳</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      Cuentas Corrientes
                    </h3>
                    <p className="text-gray-600">Cliente: <span className="font-semibold">{sheetCuentas.cliente.nombre}</span></p>
                  </div>
                </div>
                <button
                  onClick={() => setSheetCuentas(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              {/* Resumen total */}
              <div className="mt-4 bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-600 mb-1">💸 Deuda Total</p>
                    <p className="text-2xl font-bold text-red-700">
                      ${sheetCuentas.cuentas.reduce((total, cc) => total + parseFloat(cc.saldo_pendiente), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-red-500 p-3 rounded-full">
                    <span className="text-white text-xl">⚠️</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de cuentas mejorada */}
            <div className="space-y-4">
              {sheetCuentas.cuentas.map((cc, index) => (
                <div
                  key={cc.id}
                  className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-green-200 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-md">
                          💸 ${parseFloat(cc.saldo_pendiente).toFixed(2)}
                        </div>
                        <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium border border-blue-200">
                          🧾 Venta #{cc.id_venta}
                        </div>
                        <div className="bg-gray-50 text-gray-700 px-3 py-1 rounded-lg text-sm font-medium border border-gray-200">
                          📅 {cc.fecha_venta ? new Date(cc.fecha_venta).toLocaleDateString('es-AR') : 'Sin fecha'}
                        </div>
                      </div>
                      
                      {cc.descripcion && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                          <div className="flex items-start gap-2">
                            <span className="text-yellow-600 text-lg">💬</span>
                            <div>
                              <p className="text-sm font-semibold text-yellow-800 mb-1">Descripción:</p>
                              <p className="text-sm text-yellow-700">{cc.descripcion}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Mostrar detalles de la venta si existen */}
                      {cc.venta && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-blue-600 text-lg">🔍</span>
                            <h6 className="font-bold text-blue-800">Detalles de la Venta</h6>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="bg-white p-3 rounded-lg border border-blue-100">
                              <span className="font-semibold text-gray-700">Total Original:</span>
                              <span className="text-green-600 font-bold ml-2">${cc.venta.total}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-blue-100">
                              <span className="font-semibold text-gray-700">Descuento:</span>
                              <span className="text-red-600 font-bold ml-2">${cc.venta.descuento}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-blue-100">
                              <span className="font-semibold text-gray-700">Forma de pago:</span>
                              <span className="text-gray-600 ml-2 capitalize">{cc.venta.detalles_pago}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-blue-100">
                              <span className="font-semibold text-gray-700">Vendedor:</span>
                              <span className="text-gray-600 ml-2">ID #{cc.venta.id_vendedor}</span>
                            </div>
                          </div>
                          <div className="mt-2 bg-white p-3 rounded-lg border border-blue-100">
                            <span className="font-semibold text-gray-700">Fecha completa:</span>
                            <span className="text-gray-600 ml-2">
                              {cc.venta.createdAt ? new Date(cc.venta.createdAt).toLocaleString('es-AR') : 'Sin fecha'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-3 lg:flex-shrink-0">
                      <button
                        onClick={() => {
                          if (!caja) {
                            toast.error("Debe abrir una caja para cobrar cuentas corrientes");
                            return;
                          }
                          setClienteCobro({ cliente: sheetCuentas.cliente });
                          setSheetCuentas(null);
                        }}
                        className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 ${
                          !caja 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                        }`}
                        title={!caja ? "Debe abrir una caja primero" : `Cobrar deuda $${cc.saldo_pendiente}`}
                        disabled={!caja}
                      >
                        <span className="text-lg">💳</span>
                        Cobrar Deuda
                      </button>
                      <button
                        onClick={() => handleVerVenta(cc.id_venta)}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        title="Ver detalles completos de la venta"
                      >
                        <FaEye />
                        Ver Venta
                      </button>
                      <button
                        onClick={async () => {
                          toast.info("Generando ticket individual con detalles de productos...");
                          await imprimirTicketCuentaIndividual(sheetCuentas.cliente, cc);
                        }}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        title={`Imprimir ticket individual de Venta #${cc.id_venta}`}
                      >
                        <span className="text-lg">🎫</span>
                        Imprimir Ticket
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer con acciones */}
            <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex flex-col sm:flex-row gap-4">
                {caja && (
                  <button
                    onClick={() => {
                      setClienteCobro({ cliente: sheetCuentas.cliente });
                      setSheetCuentas(null);
                    }}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-4 rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">💰</span>
                    Cobrar Todas las Deudas
                  </button>
                )}
                <button
                  onClick={async () => {
                    toast.info("Generando resumen completo con detalles de productos...");
                    await imprimirTicketSaldos(sheetCuentas.cliente, sheetCuentas.cuentas);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-6 py-4 rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">🖨️</span>
                  Imprimir Resumen Total
                </button>
                <button
                  onClick={() => setSheetCuentas(null)}
                  className="px-6 py-4 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">❌</span>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </Sheet>
      )}

      {/* Sheet para mostrar detalles de la venta */}
      {ventaDetalle && (
        <Sheet isOpen={!!ventaDetalle} onClose={() => setVentaDetalle(null)}>
          <div className="max-w-2xl mx-auto p-4 bg-white shadow-md rounded-2xl border border-gray-200">
            <h3 className="text-2xl font-bold mb-6 text-blue-700 flex items-center gap-2">
              <FaEye className="text-blue-500 text-xl" />
              Venta #{ventaDetalle.id}
            </h3>

            {loadingVenta ? (
              <div className="text-center py-12 text-lg text-gray-500 animate-pulse">
                Cargando venta...
              </div>
            ) : (
              <div className="space-y-4 text-base text-gray-700">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Cliente</span>
                    <span className="font-medium">{ventaDetalle.cliente?.nombre || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Vendedor ID</span>
                    <span className="font-medium">{ventaDetalle.id_vendedor || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Total</span>
                    <span className="font-bold text-green-600">${ventaDetalle.total}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Descuento</span>
                    <span className="text-red-500 font-medium">${ventaDetalle.descuento}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Forma de pago</span>
                    {ventaDetalle.tiene_pagos_multiples && Array.isArray(ventaDetalle.detallesPago) && ventaDetalle.detallesPago.length > 0 ? (
                      <div className="space-y-2 mt-1">
                        <span className="font-semibold text-blue-700">Pagos múltiples ({ventaDetalle.detallesPago.length} formas):</span>
                        <ul className="list-disc list-inside text-sm text-gray-700">
                          {ventaDetalle.detallesPago.map((pago, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <span className="font-semibold capitalize">{pago.forma_pago.replace('_', ' ').replace('cuentaCorriente', 'Cuenta Corriente')}</span>
                              <span className="text-green-700 font-bold">${parseFloat(pago.monto).toFixed(2)}</span>
                              {pago.referencia && (
                                <span className="text-gray-500 ml-2">Ref: {pago.referencia}</span>
                              )}
                              {pago.observaciones && (
                                <span className="text-gray-400 ml-2">Obs: {pago.observaciones}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 text-green-800 font-bold">Total: ${ventaDetalle.detallesPago.reduce((sum, pago) => sum + parseFloat(pago.monto), 0).toFixed(2)}</div>
                      </div>
                    ) : (
                      <span className="font-medium capitalize">{ventaDetalle.detalles_pago}</span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Fecha</span>
                    <span className="font-medium">
                      {ventaDetalle.createdAt ? new Date(ventaDetalle.createdAt).toLocaleString() : "-"}
                    </span>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800">Productos vendidos</h4>
                  <div className="bg-gray-50 border rounded-lg p-4 max-h-60 overflow-y-auto">
                    {ventaDetalle.detalles_venta?.length > 0 ? (
                      <ul className="divide-y divide-gray-200">
                        {ventaDetalle.detalles_venta.map((detalle) => (
                          <li key={detalle.id} className="py-2">
                            <div className="flex justify-between flex-wrap gap-1">
                              <span className="font-medium">
                                {detalle.producto?.nombre_producto || "-"}
                                {detalle.descuento > 0 && (
                                  <span className="ml-2 text-sm text-red-500">(Descuento: ${detalle.descuento})</span>
                                )}
                              </span>
                              <span>
                                {parseFloat(detalle.cantidad)} x ${detalle.precio} ={" "}
                                <span className="font-semibold text-green-700">${parseFloat(detalle.subtotal).toFixed(2)}</span>
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-gray-400 text-center py-2">Sin productos</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setVentaDetalle(null)}
              className="mt-8 w-full py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-all"
            >
              Cerrar
            </button>
          </div>

        </Sheet>
      )}
    </div>
  );
};

export default CompClientes;
